/*
  Auto-demo for the Before & After slider (sections/comparison-slider.liquid).

  WHY A SEPARATE ELEMENT
  The slider itself is <comparison-slider>, defined in assets/main.js, which is
  obfuscated vendor code. That name is taken and that file is not ours to edit,
  so the demo lives in its own element placed inside the slider. It finds its
  host with closest() and drives it from the outside.

  HOW THE REVEAL IS REAL, NOT DECORATIVE
  The slider's position is a native <input type="range">. main.js listens for
  'input' on it and does exactly two things:
      sliderOverlay.style.width = value + '%'
      sliderLine.style.left     = value + '%'
  So the demo sets input.value and dispatches a bubbling 'input' event - the
  same event the browser fires while a finger drags the thumb, handled by the
  same listener. The before image reveals, the after image hides and the handle
  travels, in real time, through the component's own code path. Nothing is
  animated on top of it, and main.js is untouched.

  Positions are rounded to whole percent before being applied. A range input
  with the default step of 1 snaps to integers anyway, so rounding here makes
  the result identical in every browser instead of depending on that, and lets
  us skip the frames where the rounded value has not moved.

  THE USER ALWAYS WINS, BUT A SCROLL IS NOT A USE
  pointerdown, touchstart, mousedown and keydown stop the animation on the spot
  and record where the handle stood. All listeners are passive, so vertical
  scrolling is never blocked.

  Only a move of more than MOVED_PCT points away from that mark retires the demo
  for the rest of the page visit. The reason is that base.css stretches the range
  input over the whole component, so a thumb landing on the image to scroll lands
  on the input too, and a native range jumps to where it is touched. Retiring the
  demo on contact alone would switch the feature off for most phone visitors.
  A tap that moves the handle a point or two is scrolling; a drag across the
  image is someone using the slider.

  A cancelled demo does not come back while the component stays on screen - that
  entry is spent - so the animation never restarts under a finger that is still
  on the glass. It is armed again only by leaving the viewport and returning. An
  'input' we did not dispatch is measured the same way, which covers arrow keys
  on a focused slider.

  VIEWPORT TRIGGER
  One IntersectionObserver per element, no scroll listener. The demo needs 60%
  of the slider in view, and has to leave that state before it can fire again,
  so it never repeats while the component simply sits on screen. A component
  taller than the viewport can never reach a ratio of 0.6, which is common for a
  portrait image on a phone, so covering 60% of the viewport height counts too.

  THEME EDITOR
  connectedCallback and disconnectedCallback are the whole lifecycle. When the
  editor re-renders the section the old element is disconnected and tears down
  its observer, its listeners and any pending frame; the new one sets itself up.
  No globals, no registry, nothing to double up, nothing left running.
*/
(function () {
  'use strict';

  if (window.customElements && customElements.get('comparison-auto-demo')) return;

  var VISIBLE = 0.6;
  var COOLDOWN = 2000;
  var MOVED_PCT = 5;

  /* Un singur threshold ne-ar da callback doar la trecerea peste 0.6, si pentru
     un element mai inalt decat ecranul raportul nu ajunge niciodata acolo. Cu
     21 de trepte primim callback-uri pe tot parcursul derularii. */
  var STEPS = (function () {
    var a = [];
    for (var i = 0; i <= 20; i++) a.push(i / 20);
    return a;
  })();

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function clampPct(n, fallback) {
    var v = parseFloat(n);
    if (!isFinite(v)) v = fallback;
    return Math.min(100, Math.max(0, v));
  }

  function clampMs(n, fallback, max) {
    var v = parseFloat(n);
    if (!isFinite(v) || v < 0) v = fallback;
    return Math.min(max, v);
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  class ComparisonAutoDemo extends HTMLElement {
    connectedCallback() {
      var slider = this.closest('comparison-slider');
      var input = slider && slider.querySelector('.comparison-slider__input');
      if (!input) return;

      var mode = this.dataset.mode;
      this.mode = mode === 'off' || mode === 'once' || mode === 'every' ? mode : 'every';
      if (this.mode === 'off') return;

      this.slider = slider;
      this.input = input;

      this.posStart = clampPct(this.dataset.start, 50);
      this.posBefore = clampPct(this.dataset.before, 20);
      this.posAfter = clampPct(this.dataset.after, 80);
      this.msDelay = clampMs(this.dataset.delay, 400, 3000);
      this.msMove = clampMs(this.dataset.move, 800, 3000);
      this.msPause = clampMs(this.dataset.pause, 300, 2000);

      this.hasRun = false;
      this.userEngaged = false;
      this.gestureActive = false;
      this.gestureFrom = null;
      this.inView = false;
      this.endedAt = -Infinity;
      this.raf = 0;
      this.startedAt = 0;
      this.timeline = null;
      this.lastApplied = null;
      this.selfDispatch = false;

      /* Un singur AbortController: la teardown un abort() scoate toti
         listenerii, fara sa ținem referinte la functii legate. */
      this.ac = new AbortController();
      var opts = { passive: true, signal: this.ac.signal };
      var begin = this.beginGesture.bind(this);
      var end = this.endGesture.bind(this);
      ['pointerdown', 'touchstart', 'mousedown', 'keydown'].forEach(function (type) {
        slider.addEventListener(type, begin, opts);
      });
      /* Capatul gestului se ascultă pe window: degetul sau mouse-ul pot pleca
         de pe componenta inainte de a fi ridicate, iar un gest care nu se
         inchide ar bloca demo-ul pe veci. */
      ['pointerup', 'pointercancel', 'touchend', 'touchcancel', 'mouseup', 'keyup'].forEach(function (type) {
        window.addEventListener(type, end, opts);
      });
      input.addEventListener('input', this.handleInput.bind(this), { signal: this.ac.signal });

      this.observer = new IntersectionObserver(this.handleIntersect.bind(this), { threshold: STEPS });
      this.observer.observe(slider);
    }

    disconnectedCallback() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      if (this.ac) this.ac.abort();
      this.ac = null;
    }

    /* Raportul de intersectie nu ajunge la 0.6 pentru un element mai inalt
       decat fereastra, deci acceptam si cazul in care componenta acopera 60%
       din inaltimea ferestrei. Pe telefon, cu o imagine portret, e singurul
       criteriu care se poate indeplini. */
    isVisibleEnough(entry) {
      if (entry.intersectionRatio >= VISIBLE) return true;
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      return vh > 0 && entry.intersectionRect.height >= vh * VISIBLE;
    }

    handleIntersect(entries) {
      var entry = entries[entries.length - 1];
      var visible = this.isVisibleEnough(entry);

      if (!visible) {
        /* Armat din nou: trebuie sa iasa inainte de a putea reintra. */
        this.inView = false;
        return;
      }
      if (this.inView) return;
      this.inView = true;

      if (this.userEngaged) return;
      if (this.gestureActive) return;
      if (this.mode === 'once' && this.hasRun) return;
      if (this.raf) return;
      if (performance.now() - this.endedAt < COOLDOWN) return;
      if (prefersReducedMotion()) return;

      this.start();
    }

    start() {
      var from = clampPct(this.input.value, this.posStart);
      var d = this.msMove;
      var p = this.msPause;

      /* start -> before -> after -> start, cu pauze la capete. Segmentele de
         durata zero sunt pastrate: interpolarea le trateaza ca t = 1. */
      this.timeline = [
        { ms: this.msDelay, from: from, to: from },
        { ms: d, from: from, to: this.posBefore },
        { ms: p, from: this.posBefore, to: this.posBefore },
        { ms: d, from: this.posBefore, to: this.posAfter },
        { ms: p, from: this.posAfter, to: this.posAfter },
        { ms: d, from: this.posAfter, to: this.posStart }
      ];

      this.hasRun = true;
      this.startedAt = 0;
      this.lastApplied = String(Math.round(from));
      this.raf = requestAnimationFrame(this.tick.bind(this));
    }

    tick(now) {
      if (!this.startedAt) this.startedAt = now;
      var elapsed = now - this.startedAt;

      for (var i = 0; i < this.timeline.length; i++) {
        var seg = this.timeline[i];
        var last = i === this.timeline.length - 1;
        if (elapsed < seg.ms || last) {
          var t = seg.ms > 0 ? Math.min(1, elapsed / seg.ms) : 1;
          this.apply(seg.from + (seg.to - seg.from) * easeInOutCubic(t));
          if (last && t >= 1) {
            this.raf = 0;
            this.endedAt = now;
            return;
          }
          break;
        }
        elapsed -= seg.ms;
      }

      this.raf = requestAnimationFrame(this.tick.bind(this));
    }

    /* Acelasi drum pe care il face degetul: scriem in input si trimitem un
       'input' care urca, deci handlerul din main.js muta separatorul si
       dezvaluie imaginea in timp real. */
    apply(pct) {
      var value = String(Math.round(pct));
      if (value === this.lastApplied) return;
      this.lastApplied = value;
      this.selfDispatch = true;
      this.input.value = value;
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
      this.selfDispatch = false;
    }

    /* Primul eveniment al gestului da linia de baza, si numai el: pointerdown,
       touchstart si mousedown pot veni toate pentru o singura atingere, iar
       recitirea valorii la al doilea ar prinde deja saltul nativ al range-ului
       si ar face comparatia inutila. */
    beginGesture() {
      this.stopDemo();
      if (this.gestureActive) return;
      this.gestureActive = true;
      this.gestureFrom = clampPct(this.input.value, this.posStart);
    }

    endGesture() {
      this.gestureActive = false;
    }

    handleInput() {
      if (this.selfDispatch) return;
      this.stopDemo();

      /* Fara linie de baza nu avem ce compara, deci o stabilim acum si nu
         judecam acest eveniment. In practica un 'input' al utilizatorului e
         mereu precedat de pointerdown sau keydown. */
      if (this.gestureFrom === null) {
        this.gestureFrom = clampPct(this.input.value, this.posStart);
        return;
      }

      if (Math.abs(clampPct(this.input.value, this.gestureFrom) - this.gestureFrom) > MOVED_PCT) {
        this.retire();
      }
    }

    /* Opreste animatia si lasă separatorul exact unde e. Intrarea curenta in
       viewport rămâne consumata, deci demo-ul nu reporneste cat timp
       componenta e pe ecran; se armeaza din nou doar dupa ieșire si reintrare. */
    stopDemo() {
      if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = 0;
      }
    }

    /* Utilizatorul a folosit intenționat slider-ul: scoatem demo-ul din uz
       pentru aceasta instanta, pana la reload. */
    retire() {
      this.stopDemo();
      this.userEngaged = true;
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
  }

  customElements.define('comparison-auto-demo', ComparisonAutoDemo);
})();
